import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { connect, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { FormattedMessage } from "react-intl";
import { saveFollowUps, updateFollowUpResponse } from "../../../redux/followUps/followUpsActions";
import * as _ from "lodash";
import { Divider } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Grid } from "@material-ui/core";
import { useWorkOrderStyles } from "../workOrderDetails";
import FollowUpsRow from "./FollowUpsRow";
import FollowUpsRowEdit from "./FollowUpsRowEdit";
import { useEntryStyle } from "./RelatedWork";
import Button from "../../common/button/button";
import { STATUSES } from "../../../globals/workOrderStatus";
import DuplicateTruckRollWarningPopup from "../../../components/workOrderActions/duplicateTruckRollWarningPopup";
import { createUUId } from "../../../utils/uuid";
import useActivityQuestions from "../../activityQuestions/hooks/useActivityQuestions";
import { requestExistingActivityQuestions } from "../../../redux/activityQuestions/activityQuestionsActions";

import { generateMwaList } from "../../../utils/generateMwaList";

const useStyles = makeStyles({
    root: {
        flexGrow: 1,
    },
    card: {
        margin: "10px",
    },
    gridItem: {
        padding: "6px !important",
    },
    followUpContainer: {
        marginBottom: "25px",
    },
    gridBtn: {
        textAlign: "right",
    },
    addfollowUp: {
        marginBottom: "5px",
    },
    errorMsg: {
        color: "red",
    },
});

const disableEditStatuses = [STATUSES.BELL_BILLING_APPROVAL, STATUSES.BILLING_APPROVED, STATUSES.CANCELLED];
const FollowUpsList = (props) => {
    const { workorderData, relatedIdInformation } = props;
    const classes = Object.assign(useStyles(), useWorkOrderStyles(), useEntryStyle());
    const [deletedFollowUpIds, setDeletedFollowUpIds] = useState([]);
    const [error, setError] = useState(false);
    const [customAddFlag, setCustomAddFlag] = useState(false);
    const [duplicateWorkOrderDTOList, setDuplicateWorkOrderDTOList] = useState([]);
    const [openDuplicatePopup, setOpenDuplicatePopup] = useState(false);
    const woFollowUpList = relatedIdInformation?.followUps?.results ? relatedIdInformation?.followUps?.results : [];
    const [followUpList, setFollowUpList] = useState(woFollowUpList);
    const [editingFollowUpID, setEditingFollowUpID] = useState(null);
    const isDupCheck = useRef(false);
    const [isMandatoryFieldsComplete, setIsMandatoryFieldsComplete] = useState(true);
    const { activityQuestions, hasAllMandatoryQuestionsAnswered } = useActivityQuestions();

    const dispatch = useDispatch();
    const dispatchedFollowUpIds = useRef(new Set());

    useEffect(() => {
        if (!followUpList || followUpList.length === 0) {
            setIsMandatoryFieldsComplete(false);
            return;
        }
        followUpList.forEach((followUp) => {
            if (followUp?.followUpTpyeId?.newWorkOrderRequired && !dispatchedFollowUpIds.current.has(followUp.id)) {
                dispatch(requestExistingActivityQuestions(followUp.referenceTicketNumber, followUp.id));
                dispatchedFollowUpIds.current.add(followUp.id);
            }
        });

        //sets to TRUE if:
        // no activityQuestions
        // has activityQuestions AND all mandatory questions are answered
        // sets to FALSE if any mandatory activityQuestions are unanswered (for all followups)
        setIsMandatoryFieldsComplete(
            followUpList.filter((followup) => followup.hasActivityQuestions).every((followup) => hasAllMandatoryQuestionsAnswered(followup.uuid)),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [followUpList, activityQuestions]);

    useEffect(() => {
        const duplicateWoList = props.followUpResponse?.body?.createdFollowUpList[0]?.duplicateWorkOrdersDTO?.duplicateWorkOrderDTOList;
        // if id is returned means its save call instead of DTO check call, so dont show the pop up.
        const saveFollowupResponse = !!props.followUpResponse?.body?.createdFollowUpList[0]?.id;
        if (saveFollowupResponse) return;
        if (isDupCheck.current && duplicateWoList?.length > 0) {
            addDupDataToList(props.followUpResponse?.body?.createdFollowUpList[0]?.duplicateWorkOrdersDTO);
            setDuplicateWorkOrderDTOList(props.followUpResponse);
            setOpenDuplicatePopup(true);
            isDupCheck.current = false;
        } else if (duplicateWoList?.length === 0) {
            // if the duplicateWoList is empty, we need to update the followup
            addDupDataToList({});
        }
    }, [props.followUpResponse?.body]);
    // rebuild the local mutable copy of the followUp list
    useEffect(() => {
        if (woFollowUpList.length > 0) {
            setFollowUpList(_.cloneDeep(woFollowUpList));
        }
    }, [woFollowUpList, props.lang]);

    const readOnly = workorderData?.status === undefined || disableEditStatuses?.includes(workorderData?.status?.id);

    const addDupDataToList = (duplicateList) => {
        setFollowUpList((prevList) =>
            prevList.map((followup) =>
                followup.uuid === editingFollowUpID
                    ? {
                          ...followup,
                          validatedForDoubles: true,
                          duplicateWorkOrdersDTO: duplicateList?.duplicateWorkOrderDTOList?.length > 0 ? duplicateList : null,
                      }
                    : followup,
            ),
        );
    };

    const addEmptyRow = () => {
        setFollowUpList([
            ...followUpList,
            {
                id: null,
                uuid: createUUId(),
                followUpTpyeId: null,
                workActivityID: null,
                comments: "",
                creationDate: moment.now(),
                dueDate: "",
                new: true,
                isFollowUpActive: true,
                hasActivityQuestions: false,
            },
        ]);
    };
    const addFollowUp = () => {
        addEmptyRow();
        setCustomAddFlag(true);
        setError(false);
    };

    const removeFollowUp = (followUp, index) => {
        const deletedFollowUp = followUpList.splice(index, 1);
        if (!followUp.new) {
            deletedFollowUpIds.push(deletedFollowUp[0].id);
        }
        setFollowUpList([...followUpList]);
    };

    const cancel = () => {
        // throw away any changes
        setFollowUpList(woFollowUpList.map((w) => Object.assign({}, w))); // Deep copy
        // clear list of deleted followUps
        setDeletedFollowUpIds([]);
        setCustomAddFlag(false);
        props.onClose();
    };
    const formateDate = (iDate) => (iDate ? moment(moment(iDate)).utc().format("YYYY-MM-DD HH:mm:00") : "");

    const onSave = () => {
        var errorOnSubmit = false;
        if (followUpList.length == 0) {
            errorOnSubmit = true;
        } else if (followUpList.length > 0) {
            followUpList
                .filter((followUp) => followUp)
                .map((followUp) => {
                    if (followUp.followUpTpyeId == null) {
                        errorOnSubmit = true;
                        return;
                    }
                });
        }
        if (errorOnSubmit) {
            setError(true);
            return;
        }
        let followUpPayload = [];
        let editedArray = [...followUpList];
        if (editedArray.length != woFollowUpList.length) {
            editedArray.splice(0, woFollowUpList.length);
            //newArray.push(editedArray);
            followUpPayload = editedArray;
        }
        for (var i = 0; i < woFollowUpList.length; i++) {
            if (
                followUpList[i].followUpTpyeId.nameEn != woFollowUpList[i].followUpTpyeId.nameEn ||
                followUpList[i].comments != woFollowUpList[i].comments
            ) {
                followUpPayload.push(followUpList[i]);
            }
        }
        var followUpInfo = {
            userProfile: {
                sub: props.userInfo.sub,
                email_verified: props.userInfo.email_verified,
                adGroup: ["/CmisAdmin"],
                name: props.userInfo.name,
                preferred_username: props.userInfo.preferred_username,
                given_name: props.userInfo.given_name,
                family_name: props.userInfo.family_name,
                employeeNumber: props.technicianInfo.pein,
            },
            relatedID: workorderData.workOrderNumber,
            dueDate: workorderData.originalDueDate,
            followUpList: followUpPayload
                .filter((followUp) => followUp)
                .map((followUp) => {
                    return {
                        id: !followUp.new ? followUp.id : null,
                        taskId: workorderData.taskId,
                        followUpTpyeId: followUp.followUpTpyeId.followUpTypeID ? followUp.followUpTpyeId.followUpTypeID : followUp.followUpTpyeId,
                        creationDate: formateDate(followUp.creationDate),
                        dueDate: formateDate(followUp.dueDate ?? workorderData.dueDate),
                        comments: followUp.comments ? followUp.comments : null,
                        new: followUp.new,
                        uuid: followUp.uuid,
                        validatedForDoubles: true,
                        duplicateWorkOrdersDTO:
                            followUp.newWorkOrderRequired && followUp?.duplicateWorkOrdersDTO ? followUp?.duplicateWorkOrdersDTO : null,
                        mwaList: generateMwaList(activityQuestions.questions, followUp, props.reasons),
                    };
                }),
            deletedFollowUps: deletedFollowUpIds,
        };

        // send the update and refresh the work order
        props.saveFollowUps(followUpInfo, workorderData.workOrderNumber, true);

        setFollowUpList([...woFollowUpList]);
        // clear list of deleted followUps
        setDeletedFollowUpIds([]);

        // toggle edit mode
        props.onClose();
        setCustomAddFlag(false);
        // Clear error
        setError(false);
    };

    const proceedAddingFollowUp = () => {
        setOpenDuplicatePopup(false);
    };

    const proceedRemovingFollowUp = () => {
        let followUpResponse = _.cloneDeep(props.followUpResponse);
        delete followUpResponse.body.createdFollowUpList[0]["duplicateWorkOrdersDTO"];
        const followUpsResponse = { ...followUpResponse };
        props.updateFollowUpResponse(followUpsResponse);
        setOpenDuplicatePopup(false);
        setFollowUpList((prevList) => prevList.filter((followup) => followup.uuid != editingFollowUpID));
    };
    const handleFollowUpFieldChange = (uuid, updates) => {
        setEditingFollowUpID(uuid);
        if (updates.newWorkOrderRequired) isDupCheck.current = true;
        setFollowUpList((prevList) => prevList.map((followup) => (followup.uuid === uuid ? { ...followup, ...updates, dirty: true } : followup)));
    };

    return (
        <Grid container className={classes.root}>
            {openDuplicatePopup && (
                <DuplicateTruckRollWarningPopup
                    followUpsResponse={duplicateWorkOrderDTOList}
                    open={openDuplicatePopup}
                    handleClose={proceedAddingFollowUp}
                    handleCancel={() => proceedRemovingFollowUp()}
                    keycloak={props.keycloak}
                />
            )}
            {followUpList
                .filter((followUp) => followUp?.isFollowUpActive === true)
                .map((followUp, i) => {
                    if (customAddFlag) {
                        return (
                            <FollowUpsRowEdit
                                followUp={followUp}
                                key={followUp.id ?? followUp.uuid}
                                reasons={props.reasons}
                                handleFollowUpFieldChange={handleFollowUpFieldChange}
                                removeFollowUp={() => removeFollowUp(followUp, i)}
                                isDisabled={followUp.new}
                            />
                        );
                    }
                    return <FollowUpsRow followUp={followUp} key={followUp.id} reasons={props.reasons} />;
                })}
            {error && (
                <div className={classes.errorMsg}>
                    <FormattedMessage
                        id="alerts.follow-ups.select-mandatory-field"
                        defaultMessage="Please select the mandatory fields"
                        description="Please select the mandatory fields alert message"
                    />
                </div>
            )}
            <Grid item xs={12} className={classes.entryContainer}>
                <Grid container spacing={1}>
                    <Grid item lg={8} md={6} xs={6} />
                    {customAddFlag && (
                        <Grid item lg={4} md={6} xs={6} className={classes.gridBtn}>
                            <Button tertiary onClick={() => cancel()}>
                                <FormattedMessage
                                    id="admin.work-order-details.follow-up-list.cancel-follow-up-edit-button-caption"
                                    defaultMessage="Cancel"
                                    description="Admin work order follow-up list cancel follow-up edit button caption"
                                />
                            </Button>
                            <Button secondary disabled={!isMandatoryFieldsComplete} onClick={onSave}>
                                <FormattedMessage
                                    id="admin.work-order-details.follow-up-list.save-follow-up-edit-button-caption"
                                    defaultMessage="Save changes"
                                    description="Admin work order follow-up list save follow-up edit button caption"
                                />
                            </Button>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12} className={classes.addFollowUp}>
                        <Button secondary onClick={() => addFollowUp()} disabled={readOnly}>
                            <FormattedMessage
                                id="admin.work-order-details.follow-up-list.add-follow-up-button-caption"
                                defaultMessage="Add Follow-up +"
                                description="Admin work order follow-up list Add follow-up button caption"
                            />
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

const mapStateToProps = (state) => ({
    lang: state.domain.locale.lang,
    keycloak: state.login.keycloak,
    reasons: state.followUps.followUpReasons ? state.followUps.followUpReasons : [],
    userProfile: state.login.userProfile,
    userInfo: state.login.userInfo,
    technicianInfo: state.login.technicianInfo,
    followUpResponse: state.followUps.followUpsResponse,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            saveFollowUps,
            updateFollowUpResponse,
        },
        dispatch,
    );

export default connect(mapStateToProps, mapDispatchToProps)(FollowUpsList);
